import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { storeCostInformation } from "../../Store/TemplateStore/template.action";

import InputBox from "../../components/InputBox";
import { TextFieldValidator } from "../../components/InputBox/TextFieldValidator";
import { getCurrencySymbol } from "../../utils/Utils";
import PopoverText from "../../components/PopoverText";
import InfoIconGreen from "../../assets/info-icon-green.svg";

import { COST_INFORMATION } from "./costInformationFields.config";

import "./index.scss";

const CostInformation = React.forwardRef((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  // Get initial values from Redux store
  const {
    energyPrice,
    acidPrice,
    alkaliPrice,
    waterPrice,
    costofDownTime,
    additional_chemical_price,
    cebAcidPrice,
    cebAlkaliPrice,
    ceb_additional_chemical_price,
  } = useSelector((state) => state);
  
  const volume = useSelector((state) => state.volume);
  const mass = useSelector((state) => state.mass);
  const conversion_rate = useSelector((state) => state.conversion_rate);
  const currency = useSelector((state) => state.currency);

  // Store the base values without any conversion
  const [baseCostState, setBaseCostState] = useState({
    energyPrice,
    acidPrice,
    alkaliPrice,
    waterPrice,
    costofDownTime,
    additional_chemical_price,
    cebAcidPrice,
    cebAlkaliPrice,
    ceb_additional_chemical_price,
  });

  const [errors, setErrors] = useState({
    energyPrice: null,
    acidPrice: null,
    alkaliPrice: null,
    waterPrice: null,
    costofDownTime: null,
    additional_chemical_price: null,
    cebAcidPrice: null,
    cebAlkaliPrice: null,
    ceb_additional_chemical_price: null,
  });

  const [costFields, setCostFields] = useState(
    COST_INFORMATION(conversion_rate, currency, volume, mass)
  );

  const [noError, setNoError] = useState(true);

  const calculateConvertedValue = (value) => {
    if (value === null || value === undefined) return 0;
    return Math.round(value * conversion_rate * 100) / 100;
  };

  // Calculate displayed values with current conversion rate
  const displayCostValues = useMemo(() => ({
    energyPrice: calculateConvertedValue(baseCostState.energyPrice),
    acidPrice: calculateConvertedValue(baseCostState.acidPrice),
    alkaliPrice: calculateConvertedValue(baseCostState.alkaliPrice),
    waterPrice: calculateConvertedValue(baseCostState.waterPrice),
    costofDownTime: calculateConvertedValue(baseCostState.costofDownTime),
    additional_chemical_price: calculateConvertedValue(baseCostState.additional_chemical_price),
    cebAcidPrice: calculateConvertedValue(baseCostState.cebAcidPrice),
    cebAlkaliPrice: calculateConvertedValue(baseCostState.cebAlkaliPrice),
    ceb_additional_chemical_price: calculateConvertedValue(baseCostState.ceb_additional_chemical_price),
  }), [baseCostState, conversion_rate]);

  useEffect(() => {
    const noError = Object.values(errors).every(
      (error) => error === "" || error === null
    );
    setNoError(noError);
    props.setNoCostError(noError);
  }, [errors, props]);

  useEffect(() => {
    setCostFields(COST_INFORMATION(conversion_rate, currency, volume, mass));
  }, [conversion_rate, currency, volume, mass]);

  const costInformation = () => {
    if (noError) {
      // Store the base (unconverted) values in Redux
      dispatch(storeCostInformation(baseCostState));
    } else {
      props.setActiveStep(0);
    }
  };

  const handleInputChange = (field, value, minValue, maxValue) => {
    // Convert the input value back to base value before storing
    const baseValue = value ? (parseFloat(value) / conversion_rate) : value;
    setBaseCostState(prev => ({ ...prev, [field]: baseValue }));
    setErrors(prev => ({ ...prev, [field]: null }));

    const fieldName = t(`costInformationStrings.${field}`).split("(")[0];

    if ((value === "" && minValue !== null) || parseFloat(value) < minValue) {
      setErrors(prev => ({
        ...prev,
        [field]: `${fieldName} ${t("defaultInformationStrings.cannotLess")} ${minValue}`,
      }));
    } else if (
      maxValue !== null &&
      (parseFloat(value) < minValue || parseFloat(value) > parseFloat(maxValue.toFixed(2)))
    ) {
      setErrors(prev => ({
        ...prev,
        [field]: `${fieldName} ${t("defaultInformationStrings.cannotLess")} ${minValue} ${t(
          "defaultInformationStrings.greaterThan"
        )} ${parseFloat(maxValue.toFixed(2))}`,
      }));
    }
  };

  React.useImperativeHandle(ref, () => ({ costInformation }));

  const replaceUnit = (value) => {
    const currencySymbol = getCurrencySymbol[currency];
    if (value.includes("$")) {
      if (value.includes(t("costInformationStrings.waterPrice"))) {
        value = value.replace("<unit>", volume);
      } else if (
        value.includes(t("costInformationStrings.acidPrice")) ||
        value.includes(t("costInformationStrings.alkaliPrice")) ||
        value.includes(t("costInformationStrings.additional_chemical_price")) ||
        value.includes(t("costInformationStrings.cebAcidPrice")) ||
        value.includes(t("costInformationStrings.cebAlkaliPrice")) ||
        value.includes(t("costInformationStrings.ceb_additional_chemical_price"))
      ) {
        value = value.replace("<unit>", mass);
      }
      value = value.replace("$", currencySymbol);
    }
    return value;
  };

  return (
    <div className="lightGrayBorder section-container">
      <div className="headerTealText">
        <span>{t("costInformationStrings.constInformation")}</span>
        <PopoverText
          descriptor={<img src={InfoIconGreen} alt="icon" />}
          descriptionText={t("costInformationStrings.costOptimizationInfo")}
        />
      </div>
      <div className="grid-container">
        {Object.keys(costFields).map((key, index) => (
          <div className="grid-item" key={index}>
            <InputBox
              value={displayCostValues[key]}
              name={key}
              labelText={replaceUnit(t(`costInformationStrings.${key}`))}
              onChange={(value) =>
                handleInputChange(
                  key,
                  value,
                  costFields[key].minValue,
                  costFields[key].maxValue
                )
              }
              validator={TextFieldValidator.NUMBER_TWO_DECIMAL}
              errorMsg={errors[key]}
              maxLength={10}
            />
          </div>
        ))}
      </div>
    </div>
  );
});

export default CostInformation;
